import type React from "react";
import {useOutletContext} from "react-router";
import {Alert, Box} from "@mui/material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {useDataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import {useSupplierTransactionsQuery} from "~/pages/supplier-360/tabs/SupplierTransactions/hooks/useSupplierTransactionsQuery";
import type {SupplierDetailOutletContext} from "../../types/Supplier360Types";
import {getSupplierTransactionColumns} from "./SupplierTransactionColumns";
import {supplierGridInitialState} from "~/pages/supplier-360/components/supplierGridInitialState";

/** Highest amount first, like every Supplier 360 table (§2.21). */
const INITIAL_SORT_MODEL = [{field: 'p_spend', sort: 'desc'}] as const;

export const SupplierTransactionsTab: React.FC = () => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const gridState = useDataGridQueryState({
        initialSortModel: [...INITIAL_SORT_MODEL],
        resetPageOn: entity.id,
    });

    const {data, isFetching, isError, error} = useSupplierTransactionsQuery({
        entityId: entity.id,
        databagId: entity.databag_id,
        page: gridState.page,
        pageSize: gridState.pageSize,
        sortModel: gridState.sortModel,
        filterModel: gridState.filterModel,
    });

    const rows = data?.rows ?? [];
    const {hasActiveFilters} = gridState;

    if (isError) {
        return <Alert severity="error">{`Could not load transactions. ${(error as Error)?.message ?? ''}`}</Alert>;
    }

    return (
        <Box sx={{height: '100%', flex: 1}}>

            <CustomDataGrid
                // v2: Part name first and pinned, no Part ID; a new key drops layouts saved with the old columns.
                layoutKey="supplier-360-transactions-v2"
                initialState={supplierGridInitialState('p_name', ['s_name'])}
                rows={rows}
                columns={getSupplierTransactionColumns()}
                loading={isFetching}
                rowCount={data?.rowCount ?? 0}
                {...gridState.dataGridProps}
                // `MatPartReviewRow`'s pk is composite; the part id is unique within one entity's rows.
                getRowId={row => String(row.part)}
                // Header filters run server-side, so don't fire one request per keystroke.
                filterDebounceMs={500}
                showToolbar
                disableRowSelectionOnClick
                toolbarProps={{title: 'Transactions', showFilterPanel: false}}
                // The grid stays up when there's nothing to show, so the filters remain reachable.
                localeText={{
                    noRowsLabel: hasActiveFilters
                        ? 'No transactions match the current filters.'
                        : 'No transactions found for this supplier.',
                }}
            />
        </Box>
    );
};
